import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Star, User, Zap, Edit3, Loader2, Save } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import React, { useState, useEffect, ChangeEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/router';
import { useToast } from '@/hooks/use-toast';

interface UserProfileHeaderProps {
  email: string;
  createdAt: string;
  totalChats: number;
  savedAccountsCount: number;
  availableCredits: number | null;
  hasActiveSubscription: boolean;
  subscriptionPlanName?: string;
  initialAvatarUrl?: string | null;
  initialPersona?: string | null;
}

export function UserProfileHeader({
  email,
  createdAt,
  totalChats,
  savedAccountsCount,
  availableCredits,
  hasActiveSubscription,
  subscriptionPlanName,
  initialAvatarUrl,
  initialPersona,
}: UserProfileHeaderProps) {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return '날짜 정보 없음';
    }
  };

  const username = email.split('@')[0];
  const initial = username.charAt(0).toUpperCase();

  const { session } = useAuth();
  const { toast } = useToast();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [persona, setPersona] = useState<string>(initialPersona || '');
  const [isEditingPersona, setIsEditingPersona] = useState<boolean>(false);
  const [isSavingPersona, setIsSavingPersona] = useState<boolean>(false);
  const [personaCharCount, setPersonaCharCount] = useState<number>(0);

  const MAX_PERSONA_LENGTH: number = 300;

  useEffect(() => {
    setAvatarUrl(initialAvatarUrl || null);
    setPersona(initialPersona || '');
    setPersonaCharCount(initialPersona?.length || 0);
  }, [initialAvatarUrl, initialPersona]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "파일 크기 초과",
          description: "프로필 사진은 2MB를 넘을 수 없습니다.",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !session?.user) return;

    setIsUploading(true);

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
    const newFileName = `${session.user.id}/${Date.now()}${fileExtension ? '.' + fileExtension : '.png'}`;
    console.log("Generated fileName for upload:", newFileName);

    try {
      if (session.user.user_metadata?.avatar_path) {
        const { error: deleteError } = await supabase.storage
          .from('avatars')
          .remove([session.user.user_metadata.avatar_path]);
        if (deleteError) {
          console.warn("기존 아바타 삭제 실패:", deleteError.message);
        }
      }

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(newFileName, selectedFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      if (uploadData) {
        const { data: publicUrlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(newFileName);

        if (!publicUrlData.publicUrl) {
          throw new Error("공개 URL을 가져오는데 실패했습니다.");
        }

        const newAvatarUrl = publicUrlData.publicUrl;

        const { error: updateUserError } = await supabase.auth.updateUser({
          data: {
            avatar_url: newAvatarUrl,
            avatar_path: newFileName
          },
        });

        if (updateUserError) {
          throw updateUserError;
        }

        setAvatarUrl(newAvatarUrl);
        setSelectedFile(null);
        toast({
          title: "성공",
          description: "프로필 사진이 성공적으로 변경되었습니다.",
        });
      }
    } catch (error: any) {
      console.error("프로필 사진 업로드 오류:", error);
      toast({
        title: "오류 발생",
        description: error.message || "프로필 사진 변경 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      setAvatarUrl(initialAvatarUrl || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditPersona = (): void => {
    setIsEditingPersona(true);
  };

  const handlePersonaChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const text = e.target.value;
    if (text.length <= MAX_PERSONA_LENGTH) {
      setPersona(text);
      setPersonaCharCount(text.length);
    }
  };

  const handleSavePersona = async (): Promise<void> => {
    if (!session?.user) return;

    setIsSavingPersona(true);

    try {
      const { error: updateUserError } = await supabase.auth.updateUser({
        data: {
          persona: persona.trim()
        },
      });

      if (updateUserError) {
        throw updateUserError;
      }

      setIsEditingPersona(false);
      toast({
        title: "성공",
        description: "페르소나가 성공적으로 저장되었습니다.",
      });
    } catch (error: any) {
      console.error("페르소나 저장 오류:", error);
      toast({
        title: "오류 발생",
        description: error.message || "페르소나 저장 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPersona(false);
    }
  };

  const handleCancelPersonaEdit = (): void => {
    setPersona(initialPersona || '');
    setPersonaCharCount(initialPersona?.length || 0);
    setIsEditingPersona(false);
  };

  return (
    <Card className="overflow-hidden border bg-card shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/5 opacity-50"></div>

      <div className="relative p-6">
        <div className="flex flex-col items-center space-y-4 sm:flex-row sm:items-start sm:space-x-6 sm:space-y-0">
          <div className="relative group h-24 w-24 cursor-pointer">
            <Avatar className="h-full w-full ring-4 ring-background shadow-xl pointer-events-none">
              <AvatarImage src={avatarUrl || undefined} alt={username} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xl">
                {initial}
              </AvatarFallback>
            </Avatar>
            <div className="absolute bottom-0 right-0 rounded-full h-8 w-8 bg-background group-hover:opacity-100 opacity-100 sm:opacity-0 flex items-center justify-center pointer-events-none shadow-md">
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit3 className="h-4 w-4 text-muted-foreground" />}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="absolute inset-0 h-full w-full opacity-0 cursor-pointer z-10"
              accept="image/png, image/jpeg, image/gif"
              onChange={handleFileChange}
              disabled={isUploading}
              tabIndex={-1}
              title="프로필 사진 변경"
            />
          </div>

          <div className="space-y-3 text-center sm:text-left w-full">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold">{username}</h2>
              <p className="text-muted-foreground">{email}</p>
            </div>
            {selectedFile && !isUploading && (
              <div className="flex justify-center sm:justify-start pt-2">
                <Button onClick={handleUpload} size="sm">
                  사진 저장
                </Button>
                <Button onClick={() => {
                  setSelectedFile(null);
                  setAvatarUrl(initialAvatarUrl || null);
                }} variant="ghost" size="sm" className="ml-2">
                  취소
                </Button>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2 sm:justify-start pt-2">
              <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 rounded-full">
                <MessageSquare className="h-3 w-3" />
                <span>{totalChats}개 대화</span>
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 rounded-full">
                <User className="h-3 w-3" />
                <span>{savedAccountsCount}개 계정</span>
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 rounded-full">
                <Zap className="h-3 w-3" />
                <span>{availableCredits ?? 0} 크레딧</span>
              </Badge>
              {hasActiveSubscription && (
                <Badge className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1">
                  <Star className="h-3 w-3" />
                  <span>{subscriptionPlanName || '유료 구독자'}</span>
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              가입일: {formatDate(createdAt)}
            </p>
          </div>
        </div>

        <div className="mt-6 border-t pt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-md font-medium">나에 대해서 설명해 주세요</h3>
            {!isEditingPersona ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={handleEditPersona}
              >
                <Edit3 className="h-4 w-4 mr-1" />
                편집
              </Button>
            ) : (
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <span>{personaCharCount}/{MAX_PERSONA_LENGTH}</span>
              </div>
            )}
          </div>

          {isEditingPersona ? (
            <div className="space-y-3">
              <Textarea
                placeholder="AI와 대화할 때 활용될 당신의 페르소나를 설명해 주세요. AI가 이 설명에 맞게 대화합니다."
                value={persona}
                onChange={handlePersonaChange}
                className="w-full resize-none min-h-[100px]"
                maxLength={MAX_PERSONA_LENGTH}
              />
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelPersonaEdit}
                  disabled={isSavingPersona}
                >
                  취소
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSavePersona}
                  disabled={isSavingPersona}
                >
                  {isSavingPersona ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      저장 중...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      저장
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                이 설명은 AI와 대화할 때 활용됩니다. AI는 이 페르소나 설정에 맞게 답변을 생성합니다.
              </p>
            </div>
          ) : (
            <div className="rounded-md bg-muted/30 p-4 min-h-[80px]">
              {persona ? (
                <p className="text-sm">{persona}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  페르소나가 설정되지 않았습니다. 편집 버튼을 눌러 AI와의 대화에 활용될 페르소나를 설정해 보세요.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}